<script setup lang="ts">
// Wizard step 3 — where the export lands (`DataExportStep3.vue`, 340 ln).
//
// Rebuilt rather than ported: three copy-pasted destination cards become one loop over the
// entitlement, and the BigQuery dataset read gets the four async states it never had.
//
// NOT carried over: `frequency_options` (declared `:237`, populated `:254-259`, never read — the
// Schedule step owns frequency) and the unused `useWorkspaceStore` import (`:186`).
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import type { ExportDestinationType } from '../../../models/export.types'
import type { ExportDestinationDraft, ExportUpdateMode } from '../../../models/exportJob.types'
import { useDataExportStore } from '../../../stores/dataExportStore'

import { useAccount, useRbac } from '@/features/account'
import { useBigQueryBrowser, type BigQueryDataset } from '@/features/integrations'
import { useI18n } from '@/shared/i18n'
import {
  Button,
  Icon,
  Input,
  PlatformLogo,
  Select,
  type SelectOption,
  type UiAsyncState,
} from '@/shared/ui'

/** Legacy validated share-emails with exactly this pattern (`Step3.vue:264`). */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/
const DESTINATIONS_PERMISSION = 'export_hub_destinations'
/** `Step3.vue:280-282` — one second after the last keystroke in the project field. */
const DATASET_DEBOUNCE_MS = 1000

/** Card order (`Step3.vue:16-71`). Which of them RENDER is the entitlement's decision. */
const DESTINATION_ORDER: readonly ExportDestinationType[] = ['big_query', 'email', 'google_sheets']
const DESTINATION_LABEL_KEYS: Record<ExportDestinationType, string> = {
  big_query: 'dataExport.destination.types.bigQuery',
  email: 'dataExport.destination.types.email',
  google_sheets: 'dataExport.destination.types.googleSheets',
}
/** `:238-244`, keyed — the labels were inline English. `make_new` is the only mode whose Google
 *  Sheets wording differs ("Create New Spreadsheet" vs "Create New Table"). */
const UPDATE_MODES: readonly ExportUpdateMode[] = [
  'append',
  'replace',
  'update',
  'upsert',
  'make_new',
]
const UPDATE_MODE_LABEL_KEYS: Record<ExportUpdateMode, string> = {
  append: 'dataExport.destination.updateModes.append',
  replace: 'dataExport.destination.updateModes.replace',
  update: 'dataExport.destination.updateModes.update',
  upsert: 'dataExport.destination.updateModes.upsert',
  make_new: 'dataExport.destination.updateModes.makeNew',
}

const emit = defineEmits<{ 'update:valid': [valid: boolean] }>()
const { t } = useI18n()
const store = useDataExportStore()
const { permissions } = useRbac()
const { accountEmail } = useAccount()
const bigQuery = useBigQueryBrowser()

const datasets = ref<BigQueryDataset[]>([])
const datasetState = ref<UiAsyncState>('empty')
const emailsInput = ref('')
let debounce: ReturnType<typeof setTimeout> | null = null
let inflight: AbortController | null = null

const destination = computed<ExportDestinationDraft | null>(() => store.draft?.destination ?? null)
const destinationType = computed(() => destination.value?.type ?? null)

const entitled = computed<ExportDestinationType[]>(() => {
  const value = permissions.value[DESTINATIONS_PERMISSION]
  return Array.isArray(value) ? value.filter(isDestination) : []
})
const cards = computed(() => DESTINATION_ORDER.filter((type) => entitled.value.includes(type)))

const parsedEmails = computed(() =>
  emailsInput.value
    .split(',')
    .map((email) => email.trim())
    .filter((email) => email !== ''),
)
const invalidEmails = computed(() => parsedEmails.value.filter((e) => !EMAIL_PATTERN.test(e)))

const updateMode = computed<ExportUpdateMode | null>({
  get: () => destination.value?.update_mode ?? null,
  set: (value) => patch({ update_mode: value }),
})
const updateModeOptions = computed<SelectOption<ExportUpdateMode>[]>(() =>
  UPDATE_MODES
    // `:162` — BigQuery has no "Insert New Sheet".
    .filter((mode) => !(destinationType.value === 'big_query' && mode === 'upsert'))
    .map((mode) => ({
      value: mode,
      label:
        destinationType.value === 'google_sheets' && mode === 'make_new'
          ? t('dataExport.destination.updateModesSheet.makeNew')
          : t(UPDATE_MODE_LABEL_KEYS[mode]),
    })),
)

const projectId = computed<string>({
  get: () => destination.value?.project_id ?? '',
  set: (value) => patch({ project_id: value.trim() === '' ? null : value }),
})
const datasetId = computed<string | null>({
  get: () => destination.value?.dataset_id ?? null,
  set: (value) => patch({ dataset_id: value }),
})
const datasetOptions = computed<SelectOption<string>[]>(() =>
  datasets.value.map((dataset) => ({
    value: dataset.datasetId,
    label:
      typeof dataset['location'] === 'string'
        ? t('dataExport.destination.datasetOption', {
            dataset: dataset.datasetId,
            location: dataset['location'],
          })
        : dataset.datasetId,
  })),
)

/**
 * FIXED (ORP-18754). Legacy validated the share-email list (`:213-233`) but `handleNextDisabled(2)`
 * (`DataExportCreate.vue:359-364`) never read the result, so an invalid list was simply not emitted
 * and the user advanced with ZERO recipients. This is the signal the wizard host gates Next on.
 */
const valid = computed(() => {
  const current = destination.value
  if (!current?.type || !current.update_mode) return false
  if (current.type === 'email') return invalidEmails.value.length === 0
  if (current.type === 'big_query')
    return Boolean(current.project_id) && Boolean(current.dataset_id)
  return true
})

function isDestination(value: unknown): value is ExportDestinationType {
  return DESTINATION_ORDER.includes(value as ExportDestinationType)
}

/** Email has no vendor mark; the other two reuse 02's static platform logos. */
function logoFor(type: ExportDestinationType): string | null {
  if (type === 'big_query') return 'bigquery'
  if (type === 'google_sheets') return 'google_sheets'
  return null
}

function patch(next: Partial<ExportDestinationDraft>): void {
  const draft = store.draft
  if (!draft) return
  store.updateDraft({ destination: { ...draft.destination, ...next } })
}

/** `:328-339` — email forces `make_new`; every other destination falls back to the first mode. */
function selectDestination(type: ExportDestinationType): void {
  if (destinationType.value === type) return
  patch({ type, update_mode: type === 'email' ? 'make_new' : 'append' })
}

function scheduleDatasetLoad(): void {
  if (debounce !== null) clearTimeout(debounce)
  inflight?.abort()
  datasets.value = []
  if (destination.value?.dataset_id) patch({ dataset_id: null })
  if (destinationType.value !== 'big_query' || projectId.value.trim() === '') {
    datasetState.value = 'empty'
    return
  }
  datasetState.value = 'loading'
  debounce = setTimeout(() => void loadDatasets(), DATASET_DEBOUNCE_MS)
}

/**
 * FIXED: legacy wrapped this LOCAL read in the global page loader (`:288`, `:302`, `:306`) and
 * swallowed every failure into an empty list, which is indistinguishable from "this project has no
 * datasets". Failure is now an explicit, retryable state.
 */
async function loadDatasets(): Promise<void> {
  const controller = new AbortController()
  inflight = controller
  datasetState.value = 'loading'
  try {
    const result = await bigQuery.listDatasets(projectId.value.trim(), controller.signal)
    if (controller.signal.aborted) return
    datasets.value = result
    datasetState.value = result.length === 0 ? 'empty' : 'ready'
  } catch {
    if (controller.signal.aborted) return
    datasets.value = []
    datasetState.value = 'error'
  }
}

watch(parsedEmails, () => {
  if (destinationType.value !== 'email') return
  patch({ emails: invalidEmails.value.length > 0 ? [] : [...parsedEmails.value] })
})
watch(projectId, scheduleDatasetLoad)
watch(valid, (value) => emit('update:valid', value), { immediate: true })

onMounted(() => {
  // `:250-252` preselected the first entitled destination on EVERY mount, so stepping back into
  // this step discarded the user's choice. Preselect only when nothing has been chosen yet.
  const first = entitled.value[0]
  if (first !== undefined && destinationType.value === null) selectDestination(first)
  emailsInput.value = (destination.value?.emails ?? []).join(', ')
})

onBeforeUnmount(() => {
  if (debounce !== null) clearTimeout(debounce)
  inflight?.abort()
})
</script>

<template>
  <section class="export-destination grid gap-md" :aria-label="t('dataExport.destination.title')">
    <p v-if="cards.length === 0" class="export-destination__note m-0 text-muted">
      {{ t('dataExport.destination.empty') }}
    </p>

    <ul
      v-else
      class="export-destination__cards flex flex-wrap gap-md m-0 p-0 list-none"
      role="radiogroup"
      :aria-label="t('dataExport.destination.typeLabel')"
    >
      <li v-for="type in cards" :key="type">
        <button
          type="button"
          role="radio"
          :aria-checked="destinationType === type"
          :class="['export-destination__card', { 'is-selected': destinationType === type }]"
          @click="selectDestination(type)"
        >
          <PlatformLogo v-if="logoFor(type)" :platform="logoFor(type) ?? ''" :size="40" />
          <Icon v-else name="paper-airplane" :size="40" />
          <span>{{ t(DESTINATION_LABEL_KEYS[type]) }}</span>
        </button>
      </li>
    </ul>

    <template v-if="destinationType === 'email' || destinationType === 'google_sheets'">
      <Input
        :model-value="accountEmail ?? ''"
        :label="
          destinationType === 'email'
            ? t('dataExport.destination.primaryEmail')
            : t('dataExport.destination.primaryOwner')
        "
        disabled
      />
    </template>

    <div v-if="destinationType === 'email'" class="export-destination__field grid gap-2xs">
      <Input
        v-model="emailsInput"
        :label="t('dataExport.destination.shareWith')"
        :error="invalidEmails.length > 0"
        :error-message="
          t('dataExport.destination.invalidEmails', { emails: invalidEmails.join(', ') })
        "
      />
      <p class="export-destination__hint m-0 text-subtle text-xs">
        {{ t('dataExport.destination.shareHint') }}
      </p>
    </div>

    <template v-if="destinationType === 'big_query'">
      <Input
        v-model="projectId"
        :label="t('dataExport.destination.projectLabel')"
        :placeholder="t('dataExport.destination.projectPlaceholder')"
      />
      <div
        v-if="datasetState === 'error'"
        class="export-destination__error grid justify-items-start gap-xs text-muted"
        role="alert"
      >
        <p>{{ t('dataExport.destination.datasetsError') }}</p>
        <Button variant="outline" size="sm" @click="loadDatasets">
          {{ t('dataExport.destination.retry') }}
        </Button>
      </div>
      <Select
        v-else
        v-model="datasetId"
        :options="datasetOptions"
        :state="datasetState"
        :label="t('dataExport.destination.datasetLabel')"
        :placeholder="t('dataExport.destination.datasetPlaceholder')"
        :empty-label="t('dataExport.destination.datasetsEmpty')"
        searchable
      />
    </template>

    <div
      v-if="destinationType !== null && destinationType !== 'email'"
      class="export-destination__field grid gap-2xs"
    >
      <Select
        v-model="updateMode"
        :options="updateModeOptions"
        :label="
          destinationType === 'big_query'
            ? t('dataExport.destination.insertionMode')
            : t('dataExport.destination.writeMode')
        "
        :empty-label="t('dataExport.common.noResults')"
      />
      <p class="export-destination__hint m-0 text-subtle text-xs">
        {{ t('dataExport.destination.writeModeHint') }}
      </p>
    </div>
  </section>
</template>

<style scoped>
.export-destination .export-destination__card {
  display: grid;
  place-items: center;
  min-width: 10rem;
  min-height: 6rem;
  padding: var(--ui-space-md);
  border: 1px solid var(--ui-color-border);
  border-radius: var(--ui-radius-lg);
  background-color: var(--ui-color-surface);
  cursor: pointer;
}
.export-destination .export-destination__card.is-selected {
  border-color: var(--ui-color-accent);
  color: var(--ui-color-accent);
}
.export-destination .export-destination__error p {
  margin: 0;
}
</style>
